import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { inviteAPI } from '../services/api';
import './Auth.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

useEffect(() => {
    // Delay blur to ensure it runs after render
    const timer = setTimeout(() => {
      document.activeElement?.blur();
      // Also prevent any input from grabbing focus
      document.querySelectorAll('input').forEach(el => {
        el.setAttribute('tabindex', '-1');
        setTimeout(() => el.removeAttribute('tabindex'), 500);
      });
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);

      const pendingInvite = localStorage.getItem('pendingInvite');
      if (pendingInvite) {
        try {
          const res = await inviteAPI.accept(pendingInvite);
          localStorage.removeItem('pendingInvite');
          navigate(`/family/${res.data.familyCircle._id}`);
          return;
        } catch (inviteErr) {
          console.error('Failed to accept invite:', inviteErr);
          localStorage.removeItem('pendingInvite');
        }
      }

      // Respect redirect param (e.g. from invite page)
      const redirect = searchParams.get('redirect');
      navigate(redirect || '/');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-background">
        <div className="auth-orb auth-orb-1"></div>
        <div className="auth-orb auth-orb-2"></div>
        <div className="auth-orb auth-orb-3"></div>
      </div>

      <div className="auth-card">
        <div className="auth-logo">
          <img src="/logo.svg" alt="EchoMemoir" style={{ width: '80px', height: '80px' }} />
          <h1>EchoMemoir</h1>
          <p>Preserve voices and stories for generations</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <h2>Welcome Back</h2>

          {searchParams.get('redirect')?.includes('/invite/') && (
            <div className="invite-login-notice">
              <span>🎉</span>
              <div>
                <strong>You've been invited to join a family circle!</strong>
                <p>Sign in below to accept, or <Link to={`/register?invite=${searchParams.get('redirect')?.split('/invite/')[1]}`}>create a free account</Link> if you're new.</p>
              </div>
            </div>
          )}

          {error && <div className="auth-error">{error}</div>}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>

          <div className="form-group">
            <div className="label-row">
              <label htmlFor="password">Password</label>
              <Link to="/forgot-password" className="forgot-link">Forgot password?</Link>
            </div>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <button type="submit" className="auth-button" disabled={loading}>
            {loading ? <span className="spinner"></span> : 'Sign In'}
          </button>

          <p className="auth-switch">
            Don't have an account? <Link to="/register">Create one</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default Login;